import { useEffect, useState } from 'react';
import HiringService, { StalledCandidate } from '../../api/services/hiring.service';

// One stalled list for the whole section.
//
// Every hiring page used to call `HiringService.stalled()` on mount and
// hand the count to its own `<HiringPanel stalledCount>`. So the badge was
// re-fetched on every navigation and, for the half-second before the
// answer came back, rendered as nothing — and the candidate page rendered
// `<HiringPanel />` with no count at all, so the badge vanished entirely
// while you were reading somebody's record and reappeared when you left.
// It read as a number that could not make up its mind.
//
// A module-level cache with subscribers rather than a context provider:
// the hiring pages are separate routes under AppShell with no shared
// parent of their own, so a provider would have to go in App.tsx and be
// mounted for every section in the app.

let cache: StalledCandidate[] | null = null;
let inFlight: Promise<void> | null = null;
const listeners = new Set<(items: StalledCandidate[]) => void>();

function emit() {
  listeners.forEach((fn) => fn(cache || []));
}

/**
 * Fetch unless somebody else already is. `force` is for after a write that
 * could have changed who is stalled — finishing a checkpoint, closing
 * somebody — where the cached answer is known to be stale.
 */
export function refreshStalled(force = false): Promise<void> {
  if (inFlight) return inFlight;
  if (cache && !force) return Promise.resolve();

  inFlight = HiringService.stalled()
    .then((result) => { cache = result.items; emit(); })
    // A badge that cannot be computed is not an error worth a red banner on
    // a page that is otherwise fine. It stays at its last known value.
    .catch(() => undefined)
    .finally(() => { inFlight = null; });

  return inFlight;
}

/** Drop the cache — on sign-out, or a company switch. */
export function clearStalled(): void {
  cache = null;
  emit();
}

/**
 * The stalled list, shared. Returns the cached value immediately on every
 * page after the first, so the badge does not flicker between screens.
 */
export function useStalled(): { items: StalledCandidate[]; ids: Set<string>; reload: () => void } {
  const [items, setItems] = useState<StalledCandidate[]>(cache || []);

  useEffect(() => {
    listeners.add(setItems);
    refreshStalled();
    return () => { listeners.delete(setItems); };
  }, []);

  return {
    items,
    ids: new Set(items.map((c) => c._id)),
    reload: () => { refreshStalled(true); },
  };
}

export default useStalled;
